import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Drawer from '@material-ui/core/Drawer';
import Dialog from '@material-ui/core/Dialog';
import Button from '@material-ui/core/Button';
import Chip from '@material-ui/core/Chip';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import CircularProgress from '@material-ui/core/CircularProgress';
import { Close } from '@material-ui/icons';
import {
  DocumentTemplateType,
  INumberedDocumentView,
  WorkflowStatusType,
} from '../../../api-client/autogenerated';
import { getUserState } from '../../../features/user/selectors';
import { getProjectState } from '../../../features/project/selectors';
import {
  getActionTakenLabelOverrides,
  getDisabledActionTakenTypes,
} from '../../../features/action-taken-labels/selectors';
import { setActionCenterPrefill } from '../../../features/action-center-prefill/actions';
import { addSnackbar } from '../../../features/snackbar/actions';
import { getPermissionByUserIdAndDocumentId } from '../../../models/api/documents';
import { getQueryToolInterfaceToken, getQueryToolProjects } from '../../../models/api/query-tool';
import { copyToClipboard, documentTypeToUrl, formatDate } from '../../../scripts/utils';
import { CENTERLINE_AI_CHAT_URL } from '../centerline-ai-access';
import { CenterlineAiProvider, useCenterlineAi } from '../CenterlineAiProvider';
import Thread from '../Thread';
import Composer from '../Composer';
import { activeChat, isBusy } from '../core/chat-reducer';
import { browserStorage } from '../core/run-options';
import { TraceStep } from '../core/trace';
import { QueryToolClient } from '../core/query-tool-client';
import { FileStatus } from '../core/types';
import useReviewStyles from './review-styles';
import { submittalActionOptions } from './review-actions';
import { collectReviewFiles, documentFacts, ReviewFiles } from './review-files';
import {
  buildRfiAnswerMessage,
  buildSubmittalReviewMessage,
  REVIEW_RUN_OPTIONS,
  reviewDisplayMessage,
  SUBMITTAL_TOOL_NUDGE,
} from './review-prompts';
import {
  assessReadiness,
  readinessNotes,
  ReadinessReport,
  WAIT_INTERVAL_MS,
  WAIT_MAX_MS,
} from './review-readiness';
import { copyText, parseReview } from './review-response';
import { LastReview, loadLastReview, saveLastReview } from './review-storage';
import { ReviewKind } from './review-visibility';

/**
 * The CENTERLINE AI review pane (plan §5.4): a drawer holding one chat about one Submittal or RFI.
 * Opening it runs the review once (or opens the last one, to run again on request); the answer can
 * be copied, downloaded as a PDF, or used to prefill the action center. It never submits anything.
 */

interface PaneProps {
  open: boolean;
  onClose(): void;
  kind: ReviewKind;
  document: INumberedDocumentView;
  /** How the document is named on the page: "Submittal 08 71 00-2", "RFI 014". */
  label: string;
  docType: DocumentTemplateType;
  /** Called when a run is remembered, so the card's row can show it. */
  onSaved(review: LastReview): void;
  /** Tests pass a fake Query Tool client; otherwise one is built over `fetch`. */
  client?: QueryToolClient;
}

type Phase = 'starting' | 'checking' | 'choose' | 'blocked' | 'waiting' | 'sent' | 'restored';

const PREFILL_STATUSES = [WorkflowStatusType.UnderReview, WorkflowStatusType.ArchitectUploaded];
const MAX_CHIPS = 3;

function ReviewBody(props: Omit<PaneProps, 'open'> & { reviewFiles: ReviewFiles }) {
  const { onClose, kind, document, label, docType, reviewFiles, onSaved } = props;
  const reviewClasses = useReviewStyles();
  const dispatch = useDispatch();
  const user = useSelector(getUserState);
  const project = useSelector(getProjectState);
  const labelOverrides = useSelector(getActionTakenLabelOverrides);
  const disabledActions = useSelector(getDisabledActionTakenTypes);
  const { state, projects, actions, routeUnavailable } = useCenterlineAi();

  const actionOptions = useMemo(
    () => submittalActionOptions(labelOverrides, disabledActions, docType),
    [labelOverrides, disabledActions, docType],
  );
  const named = useMemo(
    () => reviewFiles.files.map((f) => ({ id: f.id, name: f.name })),
    [reviewFiles],
  );

  const chat = activeChat(state);
  const turns = chat?.turns ?? [];
  const hasTurns = turns.length > 0;
  const latest = turns[turns.length - 1];
  const busy = isBusy(state);
  const ready = projects.status === 'ready' && !routeUnavailable && !!state.projectId;
  const blank = !!chat && !hasTurns && !busy;

  const [phase, setPhase] = useState<Phase>('starting');
  const [report, setReport] = useState<ReadinessReport | null>(null);
  const [wantRun, setWantRun] = useState(false);
  const [assignable, setAssignable] = useState(false);
  const [snip, setSnip] = useState<string | null>(null);
  const initialised = useRef(false);
  const unmounted = useRef(false);
  const waitTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const savedTurn = useRef<string | null>(null);

  useEffect(
    () => () => {
      unmounted.current = true;
      if (waitTimer.current) clearTimeout(waitTimer.current);
    },
    [],
  );

  useEffect(() => {
    let current = true;
    getPermissionByUserIdAndDocumentId(user.id, document.id)
      .then((res) => current && setAssignable(!!res.isAssignableToDocument))
      .catch(() => current && setAssignable(false));
    return () => {
      current = false;
    };
  }, [user.id, document.id]);

  /* ---- sending the review ---- */
  const send = useCallback(
    (r: ReadinessReport) => {
      const included =
        r.verdict === 'unknown'
          ? reviewFiles.files
          : reviewFiles.files.filter((f) => r.readyIds.includes(f.id));
      const facts = documentFacts(document, label);
      const promptFiles = {
        included: included.map((f) => f.name),
        notIncluded: [...r.pending, ...r.unreadable].map((f) => f.name),
      };
      actions.sendPrepared({
        message:
          kind === 'submittal'
            ? buildSubmittalReviewMessage(facts, promptFiles, actionOptions)
            : buildRfiAnswerMessage(facts, promptFiles),
        displayMessage: reviewDisplayMessage(kind, facts),
        scope: { doc_types: reviewFiles.scope.doc_types, file_ids: included.map((f) => f.id) },
        tool: kind === 'submittal' ? SUBMITTAL_TOOL_NUDGE : null,
        options: REVIEW_RUN_OPTIONS,
      });
      setPhase('sent');
    },
    [actions, actionOptions, document, kind, label, reviewFiles],
  );

  const statusesFor = useCallback(async (): Promise<FileStatus[] | null> => {
    if (!named.length) return null;
    try {
      return await actions.fileStatus(named.map((f) => f.id));
    } catch {
      // An older Query Tool without the route, or one that cannot say: readiness is unknown.
      return null;
    }
  }, [actions, named]);

  const check = useCallback(
    async (anyway = false) => {
      setPhase('checking');
      const r = assessReadiness(named, reviewFiles.ownFileIds, await statusesFor());
      if (unmounted.current) return;
      setReport(r);
      if (anyway || r.verdict === 'all_ready' || r.verdict === 'unknown') send(r);
      else if (r.verdict === 'none_ready') setPhase('blocked');
      else if (r.pending.length === 0) send(r);
      else setPhase('choose');
    },
    [named, reviewFiles, send, statusesFor],
  );

  const wait = useCallback(() => {
    setPhase('waiting');
    const startedAt = Date.now();
    const tick = async () => {
      const r = assessReadiness(named, reviewFiles.ownFileIds, await statusesFor());
      if (unmounted.current) return;
      setReport(r);
      if (r.pending.length === 0 || Date.now() - startedAt >= WAIT_MAX_MS) {
        if (r.verdict === 'none_ready') setPhase('blocked');
        else send(r);
        return;
      }
      waitTimer.current = setTimeout(tick, WAIT_INTERVAL_MS);
    };
    waitTimer.current = setTimeout(tick, WAIT_INTERVAL_MS);
  }, [named, reviewFiles, send, statusesFor]);

  const sendNow = () => {
    if (waitTimer.current) clearTimeout(waitTimer.current);
    if (report) send(report);
  };

  /* ---- opening: the last review if there is one, otherwise a new run ---- */
  useEffect(() => {
    if (!ready || initialised.current) return;
    initialised.current = true;
    const last = loadLastReview(browserStorage(), user.id, document.id);
    if (!last) {
      setWantRun(true);
      return;
    }
    actions.restoreSession(last.sessionId).then((ok) => {
      if (unmounted.current) return;
      if (ok) setPhase('restored');
      else setWantRun(true);
    });
  }, [ready]);

  useEffect(() => {
    if (!wantRun || !ready || !blank) return;
    setWantRun(false);
    if (!reviewFiles.canRun) return;
    check();
  }, [wantRun, ready, blank]);

  const runAgain = () => {
    if (waitTimer.current) clearTimeout(waitTimer.current);
    setReport(null);
    setPhase('starting');
    actions.newChat();
    setWantRun(true);
  };

  /* ---- remember the run ---- */
  const reviewTurn = turns[0];
  useEffect(() => {
    if (!chat || !reviewTurn || !chat.sessionId) return;
    const { bot } = reviewTurn;
    if (bot.status !== 'done' || bot.restored || savedTurn.current === reviewTurn.key) return;
    savedTurn.current = reviewTurn.key;
    const parsed = parseReview(bot.answer ?? '', bot.citations, kind, actionOptions);
    const review: LastReview = {
      sessionId: chat.sessionId,
      ranAt: new Date().toISOString(),
      docType,
      ...(parsed.recommendedAction ? { recommendedAction: parsed.recommendedAction.label } : {}),
    };
    saveLastReview(browserStorage(), user.id, document.id, review);
    onSaved(review);
  }, [reviewTurn?.bot.status, chat?.sessionId]);

  /* ---- the latest answer ---- */
  const answer = latest && latest.bot.status === 'done' ? (latest.bot.answer ?? '') : '';
  const citations = latest?.bot.citations ?? [];
  const images = useMemo(
    () =>
      latest
        ? latest.bot.trace.filter((t): t is TraceStep => t.kind === 'step').flatMap((t) => t.images)
        : [],
    [latest?.bot.trace],
  );
  const restoredOnly = !!latest?.bot.restored;
  const canPrefill =
    !!answer &&
    PREFILL_STATUSES.includes(document.workflowStatus as WorkflowStatusType) &&
    (assignable || document.assignedToUserId === user.id);

  const snackbar = (message: string, severity: 'success' | 'error' = 'success') =>
    dispatch(addSnackbar({ id: Date.now(), message, severity }));

  const copy = async () => {
    try {
      await copyToClipboard(copyText(answer, citations));
      snackbar('Copied to clipboard');
    } catch {
      snackbar('Could not copy', 'error');
    }
  };

  const download = async () => {
    try {
      const { downloadReviewPdf } = await import('./review-download');
      await downloadReviewPdf({
        title: 'CENTERLINE AI review',
        projectName: project?.name,
        documentLabel: label,
        preparedFor: user.name || 'the Architect',
        date: formatDate(new Date().toISOString(), true),
        answer,
        citations,
        images,
      });
    } catch {
      snackbar('Could not create the PDF', 'error');
    }
  };

  const prefill = () => {
    const parsed = parseReview(answer, citations, kind, actionOptions);
    dispatch(
      setActionCenterPrefill({
        documentId: document.id,
        comment: parsed.proposedResponse,
        actionTaken: parsed.recommendedAction?.action,
        nonce: Date.now(),
      }),
    );
    onClose();
    // After the drawer's own transition, so the page is not locked from scrolling.
    setTimeout(
      () =>
        window.document
          .getElementById('document-action-center')
          ?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
      350,
    );
  };

  /* ---- what the body shows ---- */
  const notes = report ? readinessNotes(report, named.length) : [];
  const unavailable =
    routeUnavailable ||
    projects.status === 'failed' ||
    (projects.status === 'ready' && !state.projectId);

  const renderStatus = () => {
    if (unavailable) {
      return (
        <div className={reviewClasses.status} role="status">
          <Typography variant="h6">CENTERLINE AI is not available for this project</Typography>
          <Typography className={reviewClasses.statusText} color="textSecondary">
            It has to be switched on for the project, and your access has to include it.
          </Typography>
        </div>
      );
    }
    if (!reviewFiles.canRun) {
      return (
        <div className={reviewClasses.status} role="status">
          <Typography variant="h6">Nothing to review yet</Typography>
          <Typography className={reviewClasses.statusText} color="textSecondary">
            This submittal has no files of its own. Upload the contractor&apos;s submission first.
          </Typography>
        </div>
      );
    }
    if (phase === 'choose' || phase === 'blocked' || phase === 'waiting') {
      return (
        <div className={reviewClasses.status} role="status">
          {phase === 'waiting' ? <CircularProgress size={28} /> : null}
          {notes.map((n) => (
            <Typography key={n} className={reviewClasses.statusText}>
              {n}
            </Typography>
          ))}
          {phase === 'waiting' ? (
            <Typography className={reviewClasses.statusText} color="textSecondary">
              Waiting for the files. The review starts when they are ready, or in a few minutes with
              what is.
            </Typography>
          ) : null}
          <div className={reviewClasses.statusActions}>
            {phase === 'blocked' ? (
              <Button variant="contained" color="primary" onClick={() => check()}>
                Retry
              </Button>
            ) : (
              <>
                <Button variant="contained" color="primary" onClick={sendNow}>
                  Review now anyway
                </Button>
                {phase === 'choose' ? (
                  <Button variant="outlined" color="primary" onClick={wait}>
                    Wait
                  </Button>
                ) : null}
              </>
            )}
          </div>
        </div>
      );
    }
    return (
      <div className={reviewClasses.status} role="status">
        <CircularProgress size={28} />
        <Typography color="textSecondary">
          {phase === 'checking' ? 'Checking the document’s files…' : 'Opening CENTERLINE AI…'}
        </Typography>
      </div>
    );
  };

  const pendingIds = new Set((report?.pending ?? []).map((f) => f.id));
  const unreadableIds = new Set((report?.unreadable ?? []).map((f) => f.id));
  const chips = reviewFiles.files.slice(0, MAX_CHIPS);
  const more = reviewFiles.files.length - chips.length;

  return (
    <>
      <div className={reviewClasses.header}>
        <Typography component="h2" className={reviewClasses.headerTitle} noWrap>
          CENTERLINE AI review · {label}
        </Typography>
        <Chip size="small" variant="outlined" label="AI" className={reviewClasses.headerChip} />
        <IconButton
          aria-label="Close"
          onClick={onClose}
          size="small"
          className={reviewClasses.headerClose}
        >
          <Close />
        </IconButton>
      </div>
      <div className={reviewClasses.strip} aria-label="Documents sent to CENTERLINE AI">
        <span className={reviewClasses.stripLabel}>Reading:</span>
        {chips.map((f) => (
          <Tooltip
            key={f.id}
            title={
              pendingIds.has(f.id)
                ? 'Still being processed by CENTERLINE AI'
                : unreadableIds.has(f.id)
                  ? 'CENTERLINE AI cannot read this file'
                  : f.name
            }
          >
            <Chip
              size="small"
              variant="outlined"
              label={f.name}
              className={`${reviewClasses.fileChip} ${
                pendingIds.has(f.id) || unreadableIds.has(f.id) ? reviewClasses.fileChipPending : ''
              }`}
            />
          </Tooltip>
        ))}
        {more > 0 ? <Chip size="small" label={`+${more} more`} /> : null}
        {kind === 'rfi' ? (
          <Chip
            size="small"
            color="primary"
            variant="outlined"
            label="Drawings and Specifications sets"
          />
        ) : null}
      </div>
      <div className={reviewClasses.body}>
        {hasTurns ? (
          <div className={reviewClasses.thread}>
            <Thread />
          </div>
        ) : (
          renderStatus()
        )}
        {restoredOnly && answer ? (
          <Typography className={reviewClasses.note}>
            Snips aren&apos;t kept; Run again to regenerate them.
          </Typography>
        ) : null}
        {images.length > 0 ? (
          <div className={reviewClasses.snips}>
            <Typography variant="caption" color="textSecondary">
              Snips from the documents
            </Typography>
            <div className={reviewClasses.snipRow}>
              {images.map((src, k) => (
                <button
                  // eslint-disable-next-line react/no-array-index-key
                  key={k}
                  type="button"
                  className={reviewClasses.snip}
                  onClick={() => setSnip(src)}
                  aria-label={`Open snip ${k + 1} full size`}
                >
                  <img src={src} alt="What CENTERLINE AI looked at" />
                </button>
              ))}
            </div>
          </div>
        ) : null}
        {hasTurns && !busy ? (
          <div className={reviewClasses.actionBar}>
            {answer ? (
              <>
                <Button size="small" variant="outlined" color="primary" onClick={copy}>
                  Copy
                </Button>
                <Button size="small" variant="outlined" color="primary" onClick={download}>
                  Download PDF
                </Button>
                <Tooltip
                  title={
                    canPrefill
                      ? 'Fills the review form. You still review, edit and submit it.'
                      : 'Available while the document is under review and assigned to you.'
                  }
                >
                  <span>
                    <Button
                      size="small"
                      variant="contained"
                      color="primary"
                      disabled={!canPrefill}
                      onClick={prefill}
                    >
                      Prefill action center
                    </Button>
                  </span>
                </Tooltip>
              </>
            ) : null}
            <Button size="small" onClick={runAgain}>
              Run again
            </Button>
          </div>
        ) : null}
        {hasTurns ? <Composer /> : null}
      </div>
      <Dialog open={!!snip} onClose={() => setSnip(null)} maxWidth="lg">
        {snip ? (
          <img className={reviewClasses.snipFull} src={snip} alt="Snip from the documents" />
        ) : null}
      </Dialog>
    </>
  );
}

export default function CenterlineAiReviewPane(props: PaneProps) {
  const { open, onClose, kind, document } = props;
  const classes = useReviewStyles();
  const reviewFiles = useMemo(() => collectReviewFiles(document, kind), [document, kind]);
  return (
    <Drawer
      anchor="right"
      variant="temporary"
      open={open}
      onClose={onClose}
      ModalProps={{ keepMounted: false }}
      classes={{ paper: classes.drawerPaper }}
    >
      {open ? (
        CENTERLINE_AI_CHAT_URL ? (
          <CenterlineAiProvider
            baseUrl={CENTERLINE_AI_CHAT_URL}
            routeProjectId={document.projectId}
            issueToken={getQueryToolInterfaceToken}
            listProjects={getQueryToolProjects}
            slugs={documentTypeToUrl}
            compact
            fixedScope={reviewFiles.scope}
            client={props.client}
          >
            <ReviewBody {...props} reviewFiles={reviewFiles} />
          </CenterlineAiProvider>
        ) : (
          <div className={classes.status} role="status">
            <Typography variant="h6">CENTERLINE AI isn&apos;t configured</Typography>
            <Typography color="textSecondary">
              This environment has no CENTERLINE AI service.
            </Typography>
          </div>
        )
      ) : null}
    </Drawer>
  );
}
